import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
var _class, _class2, _AbErrorDialog;
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { BaseRCmp } from "abreact/incubator/ui/component/BaseRCmp";
import { AbErrorStackTrace } from "abreact/incubator/ui/error/AbErrorStackTrace";
import { ErrorDialogMachine } from "abreact/incubator/ui/error/ErrorDialogMachine";
import { AbButton, AbButtonType } from "abreact/misc/AbButton";
import { AbSize } from "abreact/misc/AbSize";
import { AbDialog } from "abreact/popup/AbDialog";
import { AbDialogButtonBar } from "abreact/popup/AbDialogButtonBar";
import { PopupManager } from "abreact/popup/PopupManager";
import { AbIcon } from "absdk/commons/ui/misc/AbIcon";
import * as AbWebResources from "abweb/generated/locale/AbWebResources";
import { AbWebServices } from "abweb/service/AbWebServices";
import { AbClipboard } from "abweb/utils/clipboard/AbClipboard";
import { LogAndRenderErrorPipeline } from "abweb/utils/error/LogAndRenderErrorPipeline";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { action, makeObservable, when } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
export let AbErrorDialog = observer(_class = (_class2 = (_AbErrorDialog = class AbErrorDialog extends BaseRCmp {
  //in order for any error message to be displayed, whatever you pass in as error must have a "message" field
  static async displayError(error, timestamp, description) {
    const machine = AbErrorDialog.doDisplayError(error, true, timestamp, description);
    await when(() => machine.complete);
  }
  static displayErrorWithoutUpdatingLastError(error, description) {
    return AbErrorDialog.doDisplayError(error, false, undefined, description);
  }
  static doDisplayError(error, updateLastError, timestamp, description) {
    AbErrorDialog.LOGGER.debug("Displaying error: " + error);
    if (updateLastError) {
      LogAndRenderErrorPipeline.pipeline.setLastErrorForDebounce(error);
    }
    const machine = new ErrorDialogMachine(error, timestamp, description);
    PopupManager.show(/*#__PURE__*/React.createElement(AbErrorDialog, {
      machine: machine
    }));
    return machine;
  }
  constructor(props) {
    super(props);
    _defineProperty(this, "safeResources", void 0);
    _defineProperty(this, "safeClipboard", void 0);
    _defineProperty(this, "onCopyClicked", async () => {
      const message = await this.props.machine.getClipboardText();
      this.safeClipboard.setData("text", message);
    });
    makeObservable(this);
    // An error might need to be displayed before all needed services are initialized,
    // so create 'safe' backup if a service is unavailable
    this.safeClipboard = this.calcSafeClipboard();
    this.safeResources = this.calcResources();
  }

  // 'safe' in that handle case where AbWebServices.clipboard is not yet initialized (rare)
  calcSafeClipboard() {
    try {
      return AbWebServices.clipboard;
    } catch (e) {
      AbErrorDialog.LOGGER.warn("Unable to obtain clipboard service, so creating a backup clipboard.");
      // Assume AbWebServices is not yet initialized
      return new AbClipboard();
    }
  }

  /**
   * All resources directly used by this dialog.
   * It will throw if the 'abweb' or 'abreact' resource bundle has not been initialized (so not
   * safe to use until AbWebServices and AbReactServices have been initialized, whcih load those
   * resource bundles).
   */
  calcResources() {
    // Resource lookups are co-located solely because previously the react ErrorDialog could be used before AbReact was
    // initialized (including loading the abreact resources).  Thus if fetching any abreact resource failed, it would
    // fallback to hardcode english equivalents.  This proved brittle, as all other components transitive used by the
    // ErrorDialog would introduce new abreact resource usages, which unless hardened would cause the ErrorDialog rendering
    // to fail (e.g., Popup adds a tooltip backed by an abreact resource invocation).
    // So now we don't use ReactErrorRenderer (and ErrorDialog) until AbReactServices are fully initialized
    // (including abreact resources).
    return {
      copyToClipboard: AbWebResources.copy_copyToClipboard(),
      error: AbWebResources.error(),
      close: AbWebResources.close(),
      error_showDetails: AbReactResources.error_showDetails(),
      error_detailsDialogTitle: AbReactResources.error_detailsDialogTitle(),
      error_supportInformation_instructions: AbReactResources.error_supportInstructions(),
      error_dialogTitle: AbReactResources.error_dialogTitle(),
      helpMenu_item_pfs_label: AbReactResources.helpMenu_item_pfs_label()
    };
  }
  render() {
    return /*#__PURE__*/React.createElement(AbDialog, {
      className: "ab-error-dialog",
      buttonBar: this.renderButtonBar(),
      closeButton: true,
      title: this.renderTitle(),
      onClose: () => this.onDialogClosed(),
      dataTestId: "ab-error-dialog"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ab-error-dialog-error-container"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ab-error-dialog-error-info"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ab-error-dialog-error-message"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ab-error-dialog-error-message-title"
    }, this.props.machine.currentErrorMachine.title), ":", " ", /*#__PURE__*/React.createElement("span", {
      className: "ab-error-dialog-error-message-content"
    }, this.props.machine.description ?? this.props.machine.currentErrorMachine.message)), /*#__PURE__*/React.createElement("div", null, this.safeResources.error_supportInformation_instructions)), /*#__PURE__*/React.createElement("div", {
      className: "ab-error-dialog-copy-target",
      ref: ref => {
        if (ref) {
          this._copyTargetRef = ref;
          AbWebServices.clipboard.registerForCopy(this._copyTargetRef, true, this.onCopyClicked);
        }
      }
    })));
  }
  renderButtonBar() {
    return /*#__PURE__*/React.createElement(AbDialogButtonBar, {
      cancelButton: this.safeResources.close
    }, this.renderButtons());
  }
  renderTitle() {
    return /*#__PURE__*/React.createElement("div", {
      key: "label",
      className: "ab-error-dialog-title-wrapper"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ab-error-dialog-title"
    }, /*#__PURE__*/React.createElement(AbIcon, {
      className: "ab-error-dialog-icon",
      image: "absdk:error",
      size: AbSize.LARGE,
      altText: ""
    }), /*#__PURE__*/React.createElement("div", null, this.safeResources.error_dialogTitle)), (this.props.machine.hasNextError || this.props.machine.hasPreviousError) && /*#__PURE__*/React.createElement("div", {
      key: "arrows",
      className: "ab-error-dialog-pager"
    }, /*#__PURE__*/React.createElement(AbButton, {
      className: "ab-no-border-button",
      key: "leftArrow",
      icon: "absdk:arrow-left",
      disabled: !this.props.machine.hasPreviousError,
      onClick: e => this.onPreviousClicked(e)
    }), /*#__PURE__*/React.createElement("span", {
      className: "ab-error-dialog-page-number",
      key: "countLabel"
    }, this.props.machine.errorIndex + 1 + "/" + this.props.machine.errorCount), /*#__PURE__*/React.createElement(AbButton, {
      className: "ab-no-border-button",
      key: "rightArrow",
      icon: "absdk:arrow-right",
      disabled: !this.props.machine.hasNextError,
      onClick: e => this.onNextClicked(e)
    })));
  }
  renderButtons() {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "non-concluding-buttons"
    }, this.renderErrorDetailsButton()), this.renderPFSButton());
  }
  renderCopyButton() {
    return /*#__PURE__*/React.createElement(AbButton, {
      key: "copy",
      label: this.safeResources.copyToClipboard,
      onClick: () => this.onCopyClicked(),
      dataTestId: "copy-error-details"
    });
  }
  renderErrorDetailsButton() {
    if (this.props.machine.canShowSupportInformation) {
      return /*#__PURE__*/React.createElement(AbButton, {
        key: "showDetails",
        label: this.safeResources.error_showDetails,
        onClick: () => this.onShowDetailsClicked(),
        dataTestId: "show-error-details"
      });
    }
    return null;
  }
  onDialogClosed() {
    this.props.machine.destroy();
    this.safeClipboard.unregisterForCopy(this._copyTargetRef);
  }
  onNextClicked(e) {
    this.props.machine.gotoNextError();
  }
  onPreviousClicked(e) {
    this.props.machine.gotoPreviousError();
  }
  onShowCausedByClicked() {
    if (this.props.machine.currentErrorMachine.causedBy) {
      AbErrorDialog.displayError(this.props.machine.currentErrorMachine.causedBy, this.props.machine.currentError.timestamp);
    }
  }
  async onShowDetailsClicked() {
    const {
      machine
    } = this.props;
    const supportInformation = await machine.getSupportInformation();
    if (supportInformation) {
      PopupManager.show(/*#__PURE__*/React.createElement(AbDialog, {
        title: this.safeResources.error_detailsDialogTitle,
        className: "ab-error-dialog",
        buttonBar: this.renderErrorDetailsButtons(),
        dataTestId: "ab-error-dialog-error-details"
      }, /*#__PURE__*/React.createElement(AbErrorStackTrace, {
        text: supportInformation
      })));
    }
  }
  renderErrorDetailsButtons() {
    return /*#__PURE__*/React.createElement(AbDialogButtonBar, {
      cancelButton: this.safeResources.close
    }, /*#__PURE__*/React.createElement("div", {
      className: "non-concluding-buttons"
    }, this.renderCopyButton()));
  }
  renderPFSButton() {
    // Safely retrieve pfs service (might not have been initialized)
    let pfsService = null;
    try {
      // Throws if AbWebServices (including pfs) not yet initialized
      pfsService = AbWebServices.packageForSupportService;
    } catch (err) {
      pfsService = null;
    }
    return /*#__PURE__*/React.createElement(AbButton, {
      key: "pfsButton",
      label: this.safeResources.helpMenu_item_pfs_label,
      icon: "absdk:download",
      type: AbButtonType.PRIMARY,
      onClick: () => pfsService && pfsService.createPackageForSupport(),
      disabled: pfsService == null,
      dataTestId: "download-pfs"
    });
  }
}, _defineProperty(_AbErrorDialog, "LOGGER", LogUtils.getLogger(__filename)), _AbErrorDialog), _applyDecoratedDescriptor(_class2.prototype, "onDialogClosed", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "onDialogClosed"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "onNextClicked", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "onNextClicked"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "onPreviousClicked", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "onPreviousClicked"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "onShowCausedByClicked", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "onShowCausedByClicked"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "onShowDetailsClicked", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "onShowDetailsClicked"), _class2.prototype), _class2)) || _class;
//# sourceMappingURL=AbErrorDialog.js.map
